import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import {
    createProductBrandApiV1ProductsOrgIdBrandsPost,
    deleteProductBrendApiV1ProductsOrgIdBrandsBrendIdDelete,
    getProductBrendsApiV1ProductsOrgIdBrandsGet,
    updateProductBrendApiV1ProductsOrgIdBrandsBrendIdPut,
} from '@/common/api/client/sdk.gen'
import type {
    PageProductBrandSchema,
    SrcSchemasProductProductBrandProductBrandSchema,
} from '@/common/api/client/types.gen'
import { useCurrentOrg } from '@/app/entities/org/model'

export type ProductBrand = SrcSchemasProductProductBrandProductBrandSchema

export const PRODUCT_BRANDS_QUERY_KEY = 'product-brands'

const BRANDS_PAGE_SIZE = 100

const getBrands = async (org_id: string, q?: string): Promise<PageProductBrandSchema> => {
    const res = await getProductBrendsApiV1ProductsOrgIdBrandsGet({
        path: { org_id },
        query: {
            size: BRANDS_PAGE_SIZE,
            order_by: 'name',
            direction: 'asc',
            ...(q ? { q } : {}),
        },
    })
    return res.data!
}

const createBrand = async (org_id: string, name: string): Promise<ProductBrand> => {
    const res = await createProductBrandApiV1ProductsOrgIdBrandsPost({
        path: { org_id },
        body: { name },
    })
    return res.data!
}

const renameBrand = async (org_id: string, brand_id: string, name: string): Promise<ProductBrand> => {
    const res = await updateProductBrendApiV1ProductsOrgIdBrandsBrendIdPut({
        // В URL эндпоинта есть плейсхолдер {brend_id}, отсутствующий в типах SDK — подставляем вручную.
        path: { org_id, brend_id: brand_id } as { org_id: string },
        query: { brand_id },
        body: { name },
    })
    return res.data!
}

const deleteBrand = async (org_id: string, brand_id: string): Promise<void> => {
    await deleteProductBrendApiV1ProductsOrgIdBrandsBrendIdDelete({
        path: { org_id, brend_id: brand_id } as { org_id: string },
        query: { brand_id },
    })
}

export const useBrands = (q?: string) => {
    const { data: currentOrg } = useCurrentOrg()
    const org_id = currentOrg?.id

    return useQuery({
        queryKey: [PRODUCT_BRANDS_QUERY_KEY, org_id, q ?? ''],
        queryFn: () => getBrands(org_id as string, q),
        enabled: !!org_id,
    })
}

const useInvalidateBrandRelatedQueries = () => {
    const queryClient = useQueryClient()

    return () => {
        void queryClient.invalidateQueries({ queryKey: [PRODUCT_BRANDS_QUERY_KEY] })
        void queryClient.invalidateQueries({ queryKey: ['product'] })
        void queryClient.invalidateQueries({ queryKey: ['products-infinite'] })
    }
}

export const useCreateBrand = () => {
    const queryClient = useQueryClient()
    const { data: currentOrg } = useCurrentOrg()

    return useMutation({
        mutationFn: (name: string) => createBrand(currentOrg?.id ?? '', name),
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: [PRODUCT_BRANDS_QUERY_KEY] })
        },
    })
}

export const useRenameBrand = () => {
    const invalidate = useInvalidateBrandRelatedQueries()
    const { data: currentOrg } = useCurrentOrg()

    return useMutation({
        mutationFn: ({ brandId, name }: { brandId: string; name: string }) =>
            renameBrand(currentOrg?.id ?? '', brandId, name),
        onSuccess: invalidate,
    })
}

export const useDeleteBrand = () => {
    const invalidate = useInvalidateBrandRelatedQueries()
    const { data: currentOrg } = useCurrentOrg()

    return useMutation({
        mutationFn: (brandId: string) => deleteBrand(currentOrg?.id ?? '', brandId),
        onSuccess: invalidate,
    })
}
